import router from '@/router'
import axios from 'axios'

const errorHandling = (): void => {
  router.push({
    name: 'error-general',
  })
}

const cdnApi = axios.create({
  baseURL: `//${import.meta.env.VITE_APP_CDN_HOST}/maplestory/cms/v1`,
})
// Classic World content lives beside `v1` on the same CDN
const cdnMscwApi = axios.create({
  baseURL: `//${import.meta.env.VITE_APP_CDN_HOST}/maplestory/cms/mscw`,
})

// convert all cdn image's relative path to absolute path
const _convertObject = (data: { [key: string]: string } | any) => {
  if (typeof data !== 'object' || !data) return data
  Object.keys(data).forEach((key) => {
    const value = data[key]
    if (typeof value === 'object') {
      return _convertObject(value)
    }
    if (typeof value === 'string' && value.startsWith('/media')) {
      data[key] = `//${import.meta.env.VITE_APP_CDN_HOST.replace('pre.', '')}${value}`
    }
  })
}

cdnApi.interceptors.response.use((response) => {
  _convertObject(response.data)
  return response
}, errorHandling)
cdnMscwApi.interceptors.response.use((response) => {
  _convertObject(response.data)
  return response
}, errorHandling)

const gameApi = axios.create({
  baseURL: '/api/maplestory/no-auth/v1',
  headers: {
    'Content-type': 'application/json',
  },
})
gameApi.interceptors.response.use((response) => response, errorHandling)

const rankingApi = axios.create({
  baseURL: '/api/maplestory/no-auth/ranking/v2',
  headers: {
    'Content-type': 'application/json',
  },
})
rankingApi.interceptors.response.use((response) => response, errorHandling)

const gameApiWithAuth = axios.create({
  baseURL: '/api/maplestory/v1',
  headers: {
    'Content-type': 'application/json',
    // 'X-Arena-Membership-User-No': 25872985,
  },
  withCredentials: true,
})

const myMapleApi = axios.create({
  baseURL: '/api/my-maple/v1',
  headers: { 'Content-Type': 'application/json' },
})

const chatbotApi = axios.create({
  baseURL: '/api/maplestory/no-auth/patchnotes-bot',
  headers: { 'Content-Type': 'application/json' },
})

// chatbotApi.interceptors.response.use((response) => response, errorHandling)

const mapleLaneApi = axios.create({
  baseURL: '/api/maplestory/flyway/v1',
  headers: { 'Content-Type': 'application/json' },
})

// Kept as a bare string, not just an axios instance: `/search` streams its answer, so it has to go
// through `fetch` in GlobalSearch.ts, and `fetch` needs the full URL.
// `/search` and `/feedback` have no pre deployment, so `pre` talks to live the way `live` does.
const appEnv = import.meta.env.VITE_APP_ENV
const globalSearchHost = appEnv === 'pre' || appEnv === 'live' ? 'www.nexon.com' : `${appEnv}-www.nexon.com`
const globalSearchBaseUrl = `https://${globalSearchHost}/api/maplestory/no-auth/global-search/v1`

const globalSearchApi = axios.create({
  baseURL: globalSearchBaseUrl,
  headers: { 'Content-Type': 'application/json' },
})

/**
 * Its own service root (`/exposures/v1`), and deliberately WITHOUT `gameApi`'s error interceptor:
 * exposure flags are only a visibility probe, and `errorHandling` navigates the whole app to
 * `error-general`. A flag lookup that times out must leave the page alone, not throw every visitor
 * onto an error screen.
 *
 * The id is the whole path from here, e.g. `/global-search`.
 */
const exposureApi = axios.create({
  baseURL: '/api/maplestory/exposures/v1/no-auth',
  headers: { 'Content-type': 'application/json' },
  /**
   * Axios defaults to no timeout, which the route guard cannot live with: it awaits this lookup, so
   * a server that accepts the connection and then goes quiet would hang the navigation until the
   * browser gives up. A rejection is recoverable — the store treats it as "hidden" — a hang is not.
   */
  timeout: 3000,
})

export {
  cdnApi,
  cdnMscwApi,
  chatbotApi,
  exposureApi,
  gameApi,
  gameApiWithAuth,
  globalSearchApi,
  globalSearchBaseUrl,
  mapleLaneApi,
  rankingApi,
  myMapleApi,
}
